/* 9MILLY - logika klienta dla blocks/nm-product-extras.liquid:
   <nm-complete-look> (dodaj bluzę + spodnie jednym kliknięciem) i
   <nm-notify> (formularz "powiadom o dostępności"). */

import { CartAddEvent } from '@theme/events';

class NmCompleteLook extends HTMLElement {
  #resetTimer = 0;
  #partnerTouched = false;

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    document.addEventListener('change', this.#onPageChange);
    this.#sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('change', this.#onPageChange);
    clearTimeout(this.#resetTimer);
  }

  get #button() {
    return this.querySelector('[data-ctl-add]');
  }

  get #message() {
    return this.querySelector('[data-ctl-msg]');
  }

  get #mainInputs() {
    return [...this.querySelectorAll('[data-ctl-main] input[type="radio"]')];
  }

  get #partnerInputs() {
    return [...this.querySelectorAll('fieldset:not([data-ctl-main]) input[type="radio"]')];
  }

  get #mainVariant() {
    const checked = this.#mainInputs.find((input) => input.checked);
    return checked ? Number(checked.value) : null;
  }

  get #partnerVariant() {
    const checked = this.#partnerInputs.find((input) => input.checked);
    return checked ? Number(checked.value) : null;
  }

  #setMessage(text, isError = false) {
    const node = this.#message;
    if (!node) return;
    node.textContent = text;
    if (isError) node.setAttribute('data-error', '');
    else node.removeAttribute('data-error');
  }

  #sync = () => {
    const button = this.#button;
    if (!button || button.dataset.busy) return;
    const ready = this.#mainVariant !== null && this.#partnerVariant !== null;
    button.disabled = !ready;
    button.textContent = ready ? button.dataset.label : 'Wybierz rozmiary';
  };

  // Rozmiar wybrany w karcie: ustawiamy go też w głównym wyborze wariantu
  // na stronie, a rozmiar drugiego produktu podąża za nim, dopóki klient nie
  // wybierze go ręcznie.
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    if (this.#mainInputs.includes(input)) {
      const size = input.dataset.size;
      if (!this.#partnerTouched) {
        const match = this.#partnerInputs.find((node) => node.dataset.size === size && !node.disabled);
        if (match) match.checked = true;
      }
      const pagePicker = [
        ...(this.closest('.shopify-section')?.querySelectorAll('variant-picker input[type="radio"]') || []),
      ].find((node) => node.value === size);
      if (pagePicker && !pagePicker.checked) pagePicker.click();
    } else if (this.#partnerInputs.includes(input)) {
      this.#partnerTouched = true;
    }
    this.#sync();
  };

  // Zmiana rozmiaru w głównym wyborze wariantu na stronie -> karta pokazuje to samo.
  #onPageChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || this.contains(input)) return;
    if (!input.closest('variant-picker')) return;
    window.setTimeout(() => {
      const match = this.#mainInputs.find((node) => node.dataset.size === input.value && !node.disabled);
      if (match && !match.checked) {
        match.checked = true;
        if (!this.#partnerTouched) {
          const partner = this.#partnerInputs.find((node) => node.dataset.size === input.value && !node.disabled);
          if (partner) partner.checked = true;
        }
        this.#sync();
      }
    }, 0);
  };

  #onClick = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-ctl-add]')) this.#add();
  };

  async #add() {
    const button = this.#button;
    const partner = this.#partnerVariant;
    const main = this.#mainVariant;
    if (!button || !partner || !main || button.dataset.busy) return;

    button.dataset.busy = 'true';
    button.disabled = true;
    button.textContent = 'Dodaję…';
    this.#setMessage('');

    try {
      const sections = [...document.querySelectorAll('cart-items-component')]
        .map((node) => node instanceof HTMLElement && node.dataset.sectionId)
        .filter(Boolean)
        .join(',');

      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            { id: main, quantity: 1 },
            { id: partner, quantity: 1 },
          ],
          sections,
        }),
      });
      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message || 'Nie udało się dodać zestawu.');

      const cart = await (await fetch('/cart.js')).json();
      document.dispatchEvent(
        new CartAddEvent(cart, 'nm-complete-look', {
          source: 'nm-complete-look',
          itemCount: 2,
          sections: data.sections,
        })
      );

      button.textContent = 'Dodano do koszyka ✓';
      this.#setMessage('Zestaw jest w koszyku.');
    } catch (error) {
      this.#setMessage(error instanceof Error ? error.message : 'Nie udało się dodać zestawu.', true);
      button.textContent = button.dataset.label || 'Dodaj zestaw do koszyka';
    } finally {
      clearTimeout(this.#resetTimer);
      this.#resetTimer = window.setTimeout(() => {
        delete button.dataset.busy;
        this.#sync();
      }, 1800);
    }
  }
}

class NmNotify extends HTMLElement {
  connectedCallback() {
    this.form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#onSubmit);
  }

  get form() {
    return this.querySelector('form');
  }

  // Wysyłka idzie standardową ścieżką formularza kontaktowego Shopify (z
  // ochroną antyspamową) - tu tylko pokazujemy stan "Wysyłam…".
  #onSubmit = (event) => {
    const form = /** @type {HTMLFormElement} */ (event.currentTarget);
    const button = form.querySelector('button[type="submit"]');
    if (!(button instanceof HTMLButtonElement)) return;
    button.textContent = 'Wysyłam…';
    window.setTimeout(() => {
      button.disabled = true;
    }, 0);
  };
}

if (!customElements.get('nm-complete-look')) customElements.define('nm-complete-look', NmCompleteLook);
if (!customElements.get('nm-notify')) customElements.define('nm-notify', NmNotify);
